/**
 * The grid's one active cell.
 *
 * Moved by click, the keyboard, a cell editor committing (`cell-move`), or a
 * request from outside for a cell whose row may not have arrived yet. Enter
 * or Space acts on it by clicking its control (see useCellKeys), so every
 * cell type reacts the way it does to the mouse.
 */

import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  findTextCell,
  focusGrid,
  focusIsWithGrid,
  focusOutsideGrid,
  onCellMove,
} from './dom';
import type { Cursor } from './types';

const clampIndex = (index: number, count: number) =>
  Math.max(0, Math.min(count - 1, index));

export function clampCursor(
  cursor: Cursor,
  rowCount: number,
  colCount: number
): Cursor {
  return {
    row: clampIndex(cursor.row, rowCount),
    col: clampIndex(cursor.col, colCount),
  };
}

/** A cell asked for by row id. A null column keeps the cursor's column. */
type PendingFocus = {
  rowId: string;
  columnId: string | null;
  edit: boolean;
  /** Asked for by a create: given up once the row is no longer awaited. */
  created: boolean;
};

type CursorOptions = {
  scrollRef: RefObject<HTMLElement | null>;
  activeCellRef: RefObject<HTMLTableCellElement | null>;
  /** The data rows' ids, in the order drawn. */
  rowIds: readonly string[];
  /** Every row the cursor can reach: data rows, the stand-in, the blank row. */
  rowCount: number;
  /** The visible columns' ids, in order. */
  columnIds: readonly string[];
  /** The width the pinned columns cover on each side. */
  pinnedLeft: number;
  pinnedRight: number;
  /** A created row the grid is still waiting to see in the data, or null. */
  awaitedRowId: string | null;
};

export function useCursor({
  scrollRef,
  activeCellRef,
  rowIds,
  rowCount,
  columnIds,
  pinnedLeft,
  pinnedRight,
  awaitedRowId,
}: CursorOptions) {
  const colCount = columnIds.length;
  // Where the cursor was put. A new object on every move, even to the same
  // cell, so the effects below run for each one.
  const [placed, place] = useState<Cursor>({ row: 0, col: 0 });
  // Rows can go away under the cursor (a filter, a delete); it then sits on
  // the last one rather than past the end, where Enter would do nothing. The
  // clamp is kept, so the cursor does not jump back when the rows return.
  const cursor = clampCursor(placed, rowCount, colCount);
  if (cursor.row !== placed.row || cursor.col !== placed.col) place(cursor);

  const moveTo = useCallback(
    (next: Cursor) => place(clampCursor(next, rowCount, colCount)),
    [rowCount, colCount]
  );

  // Bring the whole active cell into view at once whenever the cursor moves.
  // scrollIntoView counts a cell under the sticky header or a pinned column
  // as visible, so the visible area is worked out here without them.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const cell = activeCellRef.current;
    if (!el || !cell) return;
    const box = el.getBoundingClientRect();
    const rect = cell.getBoundingClientRect();
    const headerHeight =
      el.querySelector('thead')?.getBoundingClientRect().height ?? 0;
    const top = rect.top - box.top;
    const bottom = rect.bottom - box.top;
    if (top < headerHeight) el.scrollTop -= headerHeight - top;
    else if (bottom > el.clientHeight) el.scrollTop += bottom - el.clientHeight;
    // A pinned cell never needs scrolling sideways.
    if (cell.style.position === 'sticky') return;
    const left = rect.left - box.left;
    const right = rect.right - box.left;
    // Right-pinned columns stick inside the scroller's right padding, the
    // strip kept clear for the scroll bar, so that is hidden too.
    const paddingRight = parseFloat(getComputedStyle(el).paddingRight) || 0;
    const visibleRight = el.clientWidth - paddingRight - pinnedRight;
    if (left < pinnedLeft) el.scrollLeft -= pinnedLeft - left;
    else if (right > visibleRight) el.scrollLeft += right - visibleRight;
  }, [placed, scrollRef, activeCellRef, pinnedLeft, pinnedRight]);

  // Enter or Tab from inside a cell editor: the editor commits, the cursor
  // moves on from where it is.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    return onCellMove(el, ({ rows, cols }) => {
      const col = cursor.col + cols;
      if (cols !== 0 && (col < 0 || col >= colCount)) {
        // Tab past the first or last cell leaves the grid, as it does from a
        // cell that is not being edited. After a frame, once the editor has
        // closed, so its blur does not commit a second time.
        requestAnimationFrame(() => focusOutsideGrid(el, cols > 0 ? 1 : -1));
        return;
      }
      place(clampCursor({ row: cursor.row + rows, col }, rowCount, colCount));
    });
  }, [scrollRef, cursor.row, cursor.col, rowCount, colCount]);

  // A cell asked for with `edit` opens for typing once the cursor is on it.
  // Declared before the pending focus below, so it sees the move that focus
  // makes on the next render rather than the cell the cursor is leaving.
  const editAfterMoveRef = useRef(false);
  useEffect(() => {
    if (!editAfterMoveRef.current) return;
    editAfterMoveRef.current = false;
    findTextCell(activeCellRef.current)?.click();
  }, [placed, activeCellRef]);

  const pendingFocusRef = useRef<PendingFocus | null>(null);
  /** `asked`: called by the caller just now, rather than on a later render. */
  const resolvePendingFocus = (asked: boolean) => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    const row = rowIds.indexOf(pending.rowId);
    const col =
      pending.columnId === null
        ? cursor.col
        : columnIds.indexOf(pending.columnId);
    if (row < 0 || col < 0) {
      // The grid stopped waiting for the created row: so does the cursor, so
      // it does not jump there if the row turns up much later.
      if (pending.created && awaitedRowId !== pending.rowId) {
        pendingFocusRef.current = null;
      }
      return;
    }
    pendingFocusRef.current = null;
    place({ row, col });
    // A row that arrives late must not take the keys from wherever they went
    // in the meantime (another input, say): the cursor moves, but neither the
    // grid nor the cell's editor takes focus.
    const root = scrollRef.current;
    if (asked || focusIsWithGrid(root)) {
      editAfterMoveRef.current = pending.edit;
      focusGrid(root);
    }
  };
  // A row asked for before it was in the data is moved to once it arrives.
  useEffect(() => resolvePendingFocus(false));

  /** Move to a cell by row id, now or as soon as its row is in the data. */
  const focusCell = (rowId: string, columnId: string | null, edit: boolean) => {
    pendingFocusRef.current = { rowId, columnId, edit, created: false };
    resolvePendingFocus(true);
  };

  /**
   * Move to a row just created, once it is drawn. Given up if the grid stops
   * waiting for it (see useStandIn).
   */
  const focusCreatedRow = (
    rowId: string,
    columnId: string | null,
    edit: boolean
  ) => {
    pendingFocusRef.current = { rowId, columnId, edit, created: true };
  };

  // Clicking a cell selects it. Stable, so a row that has not changed does
  // not redraw because the grid did.
  const selectCell = useCallback(
    (row: number, col: number) => {
      place({ row, col });
      // Keep the keys on the grid unless the click lands in something that
      // takes focus itself.
      const root = scrollRef.current;
      requestAnimationFrame(() => {
        if (document.activeElement === document.body) focusGrid(root);
      });
    },
    [scrollRef]
  );

  return {
    cursor,
    /** Move to a cell, kept inside the grid. */
    moveTo,
    /**
     * Put the cursor on a row that is about to exist (the stand-in for a row
     * being added); it is kept inside the grid once that row is drawn.
     */
    place,
    focusCell,
    focusCreatedRow,
    selectCell,
  };
}

export type CursorControls = ReturnType<typeof useCursor>;
